import { useList } from "@refinedev/core";
import { useCallback } from "react";
import { MediaProjectPublicationBase } from "pages/media/types";
import { useWsSubscription } from "hooks/useWsSubscription";
import { NOTIFICATIONS_EVENT_TYPE, WSEvent } from "../../../../store/websocketStore";

// The publication endpoint isn't touched by the MED-6885 migration — a
// publication record still exists for every instance that has ever been
// live, so list membership is the batch "is this instance live" signal for
// pages that need it across several instances at once (a single instance's
// precise state should go through `usePublicationStatus` instead).
export const useProjectPublications = (projectId?: string) => {
  const { data, isLoading, refetch } = useList<MediaProjectPublicationBase>({
    resource: `media/projects/${projectId}/publications`,
    queryOptions: {
      enabled: Boolean(projectId),
    },
  });

  // Keep the list fresh via the same PublicationStatusChanged event
  // usePublicationStatus reacts to, instead of relying on a one-shot fetch.
  const handlePublicationStatusChanged = useCallback(
    (event: WSEvent) => {
      if (event.event !== NOTIFICATIONS_EVENT_TYPE.PublicationStatusChanged)
        return;
      void refetch();
    },
    [refetch]
  );

  useWsSubscription({ type: "project", id: projectId }, handlePublicationStatusChanged);

  const publications = data?.data;

  const isInstanceLive = useCallback(
    (instanceId?: string) =>
      Boolean(
        instanceId &&
          publications?.some((p) => p.instance_id === instanceId)
      ),
    [publications]
  );

  return { publications, isInstanceLive, isLoading };
};
